import { GENERIC_POLICY_TYPE_LABELS } from '../../domain/policies/policyPacks';
import type { Incident } from '../../services/incidentsApi';
import { buyerConditionDisplayName } from '../../domain/buyerFacing/conditionDisplayNames';

const INCIDENT_TYPE_LABELS: Record<string, string> = {
  ppe_violation: 'PPE non-compliance',
  ppe_compliance: 'PPE non-compliance',
  occupancy: 'Occupancy alert',
  region_entrance: 'Unauthorized entry',
  region_exiting: 'Area exit',
  line_crossing: 'Line crossing',
  dwell_violation: 'Restricted-area entry',
  loitering: 'Extended loitering',
  zone_violation: 'Restricted-area entry',
  zone_intrusion: 'Restricted-area entry',
  pose_violation: 'Pose / posture',
  intrusion: 'Unauthorized entry',
  wet_floor: 'Wet floor hazard',
  safety_alert: 'Safety alert',
  safety: 'Safety',
  unknown: 'Observation',
  other: 'Observation',
  ppe_violation_person: 'PPE non-compliance',
  pedestrian_near_moving_forklift: 'Forklift-pedestrian proximity',
  forklift_proximity: 'Forklift-pedestrian proximity',
  pedestrian_forklift: 'Forklift-pedestrian proximity',
  worker_down: 'Worker down',
  forklift_speeding: 'Forklift speeding',
};

export const KNOWN_INCIDENT_TYPE_KEYS = Object.keys(INCIDENT_TYPE_LABELS);

function titleCase(value: string): string {
  const text = String(value || '').trim();
  if (!text) return '';
  return text
    .replace(/_/g, ' ')
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(' ');
}

export function incidentTypeLabel(raw: unknown): string {
  const key = String(raw || '').trim().toLowerCase();
  if (!key) return 'Incident';
  const genericPolicyLabel = (GENERIC_POLICY_TYPE_LABELS as Record<string, string>)[key];
  return buyerConditionDisplayName(
    INCIDENT_TYPE_LABELS[key] || genericPolicyLabel || titleCase(key),
    key,
  );
}

export function incidentTypeLabelForRow(
  row: Incident | Record<string, unknown> | null | undefined,
  resolvePolicyType?: (row: Incident | Record<string, unknown> | null | undefined) => string | null | undefined,
): string {
  const record = (row || {}) as any;
  const canonicalLabel = String(record?.canonical_labels?.incident_label || '').trim();
  if (canonicalLabel) {
    return buyerConditionDisplayName(canonicalLabel, record?.incident_type);
  }
  const incidentType = String(record?.incident_type || record?.title || '').trim();
  const incidentTypeKey = incidentType.toLowerCase();
  if (incidentTypeKey && incidentTypeKey !== 'unknown' && incidentTypeKey !== 'other') {
    return incidentTypeLabel(incidentTypeKey);
  }

  const policyType = String(record?.policy_type || record?.triggered_by?.policy_type || '').trim();
  if (policyType) {
    return incidentTypeLabel(policyType);
  }

  const resolvedPolicyType = String(resolvePolicyType?.(record) || '').trim();
  if (resolvedPolicyType) {
    return incidentTypeLabel(resolvedPolicyType);
  }

  const taxonomyCategory = String(record?.taxonomy?.category || '').trim();
  if (taxonomyCategory) {
    return incidentTypeLabel(taxonomyCategory);
  }

  const taxonomyLabel = String(record?.taxonomy?.label || '').trim();
  const policyName = String(record?.policy_name || record?.triggered_by?.policy_name || '').trim();
  const taxonomyLooksOperatorFriendly =
    taxonomyLabel &&
    !/[._-]/.test(taxonomyLabel) &&
    !/\bpolicy\b/i.test(taxonomyLabel) &&
    taxonomyLabel.toLowerCase() !== policyName.toLowerCase();
  if (taxonomyLooksOperatorFriendly) {
    return taxonomyLabel;
  }

  return incidentTypeLabel(incidentTypeKey);
}
